import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EditableDirectDebit, MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT } from '@platform-frontend/marketplace';
import { DirectDebitValidators } from '@platform-frontend/payment-util';
import { PlannedError, TypedChanges } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../checkout-transloco-scope';
import { IbanDetailsService } from '../iban-details.service';
import { IbanDetails } from '../model/iban-details';

interface DirectDebitForm {
  type: FormControl<'DIRECT_DEBIT'>;
  id: FormControl<number>;
  accountOwner: FormControl<string>;
  iban: FormControl<string>;
  bank: FormControl<string>;
  bic: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'yat-direct-debit-form',
  templateUrl: './direct-debit-form.component.html',
  styleUrls: ['./direct-debit-form.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class DirectDebitFormComponent implements OnChanges, OnInit {
  constructor(private ibanDetailsService: IbanDetailsService, private matomoTracker: MatomoTracker) {}

  @Input()
  input: Partial<EditableDirectDebit> | null = null;

  @Input()
  buttonsClassName: string = '';

  @Output()
  save: EventEmitter<EditableDirectDebit> = new EventEmitter();

  @Output()
  cancelMethod: EventEmitter<void> = new EventEmitter();

  @ContentChild('formButtons', { static: true })
  formButtons: TemplateRef<void>;

  ibanDetailsStatus: 'empty' | 'loaded' = 'empty';

  readonly SEPA_NOT_AVAILABLE_VALIDATION = { sepa: true };
  readonly MATOMO_EVENT_CATEGORY_PAYMENT = MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT;

  directDebitForm = new FormGroup<DirectDebitForm>({
    type: new FormControl('DIRECT_DEBIT', { nonNullable: true }),
    id: new FormControl(0, { nonNullable: true }),
    accountOwner: new FormControl('', { nonNullable: true, validators: [Validators.required, DirectDebitValidators.accountOwner] }),
    iban: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, DirectDebitValidators.iban],
      asyncValidators: control => this.ibanDetailsValidator(control)
    }),
    bank: new FormControl('bank', {
      nonNullable: true
    }),
    bic: new FormControl('bic', {
      nonNullable: true
    })
  });

  ngOnChanges(changes: TypedChanges<DirectDebitFormComponent>): void {
    if (changes.input) {
      this.directDebitForm.reset(
        this.input ? { ...this.input, bank: this.input.bank ?? 'bank', bic: this.input.bic ?? 'bic' } : { type: 'DIRECT_DEBIT', id: 0 }
      );
    }
  }

  ngOnInit(): void {
    this.directDebitForm.controls.iban.valueChanges
      .pipe(
        tap(() => this.resetIbanDetailsControlsOnSyncValidationErrors()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private resetIbanDetailsControlsOnSyncValidationErrors(): void {
    const ibanErrors = this.directDebitForm.controls.iban.errors;
    if (ibanErrors !== null && ibanErrors !== this.SEPA_NOT_AVAILABLE_VALIDATION) {
      this.resetIbanDetailsControls();
    }
  }

  private ibanDetailsValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    return this.ibanDetailsService.getIbanDetails(control.value).pipe(
      switchMap(ibanDetails => this.handleIbanDetails(ibanDetails)),
      catchError(error => this.handleIbanDetailsError(error))
    );
  }

  private handleIbanDetails(ibanDetails: IbanDetails): Observable<ValidationErrors | null> {
    this.setIbanDetailsControls(ibanDetails.bank, ibanDetails.bic);
    return ibanDetails.sepa ? of(null) : of(this.SEPA_NOT_AVAILABLE_VALIDATION);
  }

  private setIbanDetailsControls(bank: string, bic: string): void {
    this.directDebitForm.controls.bank.setValue(bank);
    this.directDebitForm.controls.bic.setValue(bic);
    this.directDebitForm.controls.bank.setErrors(null);
    this.directDebitForm.controls.bic.setErrors(null);
    this.ibanDetailsStatus = 'loaded';
  }

  private handleIbanDetailsError(error: any): Observable<ValidationErrors | null> {
    this.resetIbanDetailsControls();
    if (error instanceof PlannedError && error.errorType === 'IBAN_INVALID') {
      return of({ iban: true });
    } else {
      this.setIbanDetailsControlsError();
      return of(null);
    }
  }

  private resetIbanDetailsControls(): void {
    this.ibanDetailsStatus = 'empty';
    this.directDebitForm.controls.bank.reset();
    this.directDebitForm.controls.bic.reset();
  }

  private setIbanDetailsControlsError(): void {
    this.directDebitForm.controls.bank.setErrors({ ibanDetails: true });
    this.directDebitForm.controls.bic.setErrors({ ibanDetails: true });
  }

  submitForm(value: EditableDirectDebit): void {
    this.save.next(value);
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Confirm_PaymentMethodForm', 'DirectDebit');
  }

  cancel(): void {
    this.cancelMethod.emit();
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Cancel_PaymentMethodForm', 'DirectDebit');
  }
}
